import React from "react";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Box,
  useMediaQuery,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";
import LogoutButton from "../LogoutButton/LogoutButton";
import {
  LocalShipping as LocalShippingIcon,
  Group as GroupIcon,
  AttachMoney as AttachMoneyIcon,
  CarRental as CarRentalIcon,
  MoneyOutlined,
  History as HistoryIcon,
  ReportProblem as ReportProblemIcon,
  PeopleAlt as PeopleAltIcon,
  LibraryBooks as LibraryBooksIcon,
  HowToReg as HowToRegIcon,
  DocumentScanner as DocumentScannerIcon,
  SupportAgent as SupportAgentIcon,
  ExpandMore as ExpandMoreIcon,
  Route as RouteIcon,
  Menu as MenuIcon,
  Apps as AppsIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import logo from "../../../assets/images/logo.png";
import useAuth from "../../../hooks/useAuth";

interface NavigationBarProps {
  children: React.ReactNode;
  sidebar?: React.ReactElement;
}

const NavigationBar = ({ children, sidebar }: NavigationBarProps) => {
  const { role } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const compactHeader = useMediaQuery(theme.breakpoints.down(1700));
  const [modulesOpen, setModulesOpen] = React.useState(false);

  const [sidebarOpen, setSidebarOpen] = React.useState<boolean>(() => {
    if (!sidebar) return false;
    return !isSmallScreen;
  });
  const [openMenu, setOpenMenu] = React.useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  React.useEffect(() => {
    if (!compactHeader) setModulesOpen(false);
  }, [compactHeader]);

  React.useEffect(() => {
    if (isSmallScreen) setSidebarOpen(false);
    setModulesOpen(false);
    setOpenMenu(null);
    setMenuAnchorEl(null);
    // Close navigation overlays after following a link, including browser Back.
  }, [location.pathname, isSmallScreen]);

  const fleetAndRentalMenuItems = [
    { text: "Fleet", path: "/fleet", icon: <LocalShippingIcon /> },
    { text: "Rental", path: "/rental", icon: <DocumentScannerIcon /> },
  ];

  const salesMenuItems = [
    { text: "Sales", path: "/sales", icon: <MoneyOutlined /> },
  ];

  const operationsMenuItems = [
    {
      text: "Operations",
      path: role?.includes("Operations")
        ? "/operations"
        : "/operations/dispatchers-passwords",
      icon: <LibraryBooksIcon />,
    },
  ];

  const isAccountingPath = location.pathname.startsWith("/accounting");
  const isSafetyPath = location.pathname.startsWith("/safety");
  const isRecruitingPath = location.pathname.startsWith("/recruiting");
  const isRentalPath = location.pathname.startsWith("/rental");
  const isPermitsPath = location.pathname.startsWith("/permits");
  const isTicketsPath = location.pathname.startsWith("/tickets");
  const isPathSelected = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isFleetAndRentalPath = isPathSelected("/fleet") || isRentalPath;
  const isCRMPath =
    location.pathname.startsWith("/fleet-crm") ||
    location.pathname.startsWith("/hire-crm");

  const fleetCRMMenuItems = [
    { text: "Fleet CRM", icon: <CarRentalIcon />, path: "/fleet-crm" },
  ];
  const hireCRMMenuItems = [
    { text: "Hire CRM", icon: <PeopleAltIcon />, path: "/hire-crm" },
  ];

  const adminMenuItems = [
    { text: "Logs", icon: <HistoryIcon />, path: "/logs" },
  ];

  const closeModuleMenu = () => {
    setOpenMenu(null);
    setMenuAnchorEl(null);
  };

  const renderModuleButton = ({
    text,
    to,
    icon,
    selected,
  }: {
    text: string;
    to: string;
    icon: React.ReactElement;
    selected: boolean;
  }) => {
    const button = (
      <Button
        color="primary"
        aria-label={text}
        component={RouterLink}
        to={to}
        startIcon={isSmallScreen ? undefined : icon}
        variant={selected ? "outlined" : "text"}
        sx={{
          minWidth: isSmallScreen ? 48 : "auto",
          px: isSmallScreen ? 1 : 2,
        }}
      >
        {isSmallScreen ? icon : text}
      </Button>
    );

    if (!isSmallScreen) {
      return button;
    }

    return (
      <Tooltip title={text} arrow>
        {button}
      </Tooltip>
    );
  };

  const renderModuleGroup = ({
    text,
    icon,
    selected,
    items,
  }: {
    text: string;
    icon: React.ReactElement;
    selected: boolean;
    items: { text: string; path: string; icon: React.ReactElement }[];
  }) => {
    const menuId = `${text.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-menu`;
    const isOpen = openMenu === text;
    const button = (
      <Button
        color="primary"
        aria-label={text}
        onClick={(event) => {
          setOpenMenu(text);
          setMenuAnchorEl(event.currentTarget);
        }}
        startIcon={isSmallScreen ? undefined : icon}
        endIcon={isSmallScreen ? undefined : <ExpandMoreIcon />}
        variant={selected ? "outlined" : "text"}
        sx={{
          minWidth: isSmallScreen ? 48 : "auto",
          px: isSmallScreen ? 1 : 2,
        }}
        aria-controls={isOpen ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : undefined}
      >
        {isSmallScreen ? icon : text}
      </Button>
    );

    return (
      <React.Fragment key={text}>
        {isSmallScreen ? (
          <Tooltip title={text} arrow>
            {button}
          </Tooltip>
        ) : (
          button
        )}
        <Menu
          id={menuId}
          anchorEl={menuAnchorEl}
          open={isOpen}
          onClose={closeModuleMenu}
        >
          {items.map((item) => (
            <MenuItem
              key={item.text}
              component={RouterLink}
              to={item.path}
              selected={isPathSelected(item.path)}
              onClick={closeModuleMenu}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>{item.text}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </React.Fragment>
    );
  };

  if (
    location.pathname === "/login" ||
    location.pathname === "/unauthorized" ||
    location.pathname === "*"
  ) {
    return <>{children}</>;
  }
  const drawerWidth = 240;
  const collapsedWidth = 60;
  const renderedSidebar = sidebar
    ? React.cloneElement(sidebar, { open: sidebarOpen, toggle: toggleSidebar })
    : null;

  const mobileModules = [
    ...(role?.includes("Fleet")
      ? [...fleetAndRentalMenuItems, ...salesMenuItems]
      : []),
    ...(role?.includes("User")
      ? [{ text: "Tickets", path: "/tickets", icon: <SupportAgentIcon /> }]
      : []),
    ...(role?.some((r) => ["Employee", "HR"].includes(r))
      ? [{ text: "HR", path: "/hr", icon: <GroupIcon /> }]
      : []),
    ...(role?.includes("Accounting")
      ? [{ text: "Accounting", path: "/accounting", icon: <AttachMoneyIcon /> }]
      : []),
    ...(role?.some((r) => ["Recruiting", "Orientation"].includes(r))
      ? [{ text: "Recruiting", path: "/recruiting", icon: <HowToRegIcon /> }]
      : []),
    ...(role?.includes("Safety")
      ? [{ text: "Safety", path: "/safety", icon: <ReportProblemIcon /> }]
      : []),
    ...(role?.some((r) =>
      ["Operations", "OperationsManager", "OperationsAdmin"].includes(r),
    )
      ? operationsMenuItems
      : []),
    ...(role?.includes("Permits")
      ? [{ text: "Permits", path: "/permits", icon: <RouteIcon /> }]
      : []),
    ...(role?.includes("FleetCRM") ? fleetCRMMenuItems : []),
    ...(role?.includes("HireCRM") ? hireCRMMenuItems : []),
    ...(role?.includes("Admin") ? adminMenuItems : []),
  ];

  return (
    <>
      <AppBar
        position="fixed"
        color="default"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: "64px !important",
            gap: 1,
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {sidebar && isSmallScreen && (
              <IconButton
                aria-label="Open section navigation"
                aria-expanded={sidebarOpen}
                onClick={toggleSidebar}
              >
                <MenuIcon />
              </IconButton>
            )}
            <IconButton component={RouterLink} to="/" aria-label="Home">
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{
                  height: { xs: 28, sm: 40 },
                  width: "auto",
                  maxWidth: { xs: 90, sm: "none" },
                  objectFit: "contain",
                }}
              />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: compactHeader ? "none" : "flex",
              flexGrow: 1,
              justifyContent: "center",
              gap: 2,
            }}
          >
            {role?.includes("Fleet") &&
              renderModuleGroup({
                text: "Fleet",
                icon: <LocalShippingIcon />,
                selected: isFleetAndRentalPath,
                items: fleetAndRentalMenuItems,
              })}
            {role?.includes("Fleet") &&
              salesMenuItems.map((item) => (
                <React.Fragment key={item.text}>
                  {renderModuleButton({
                    text: item.text,
                    to: item.path,
                    icon: item.icon,
                    selected: location.pathname === item.path,
                  })}
                </React.Fragment>
              ))}
            {role?.includes("User") &&
              renderModuleButton({
                text: "Tickets",
                to: "/tickets",
                icon: <SupportAgentIcon />,
                selected: isTicketsPath,
              })}
            {(role?.includes("Employee") || role?.includes("HR")) &&
              renderModuleButton({
                text: "HR",
                to: "/hr",
                icon: <GroupIcon />,
                selected: location.pathname.startsWith("/hr"),
              })}
            {role?.includes("Accounting") &&
              renderModuleButton({
                text: "Accounting",
                to: "/accounting",
                icon: <AttachMoneyIcon />,
                selected: isAccountingPath,
              })}
            {(role?.includes("Recruiting") || role?.includes("Orientation")) &&
              renderModuleButton({
                text: "Recruiting",
                to: "/recruiting",
                icon: <HowToRegIcon />,
                selected: isRecruitingPath,
              })}
            {role?.includes("Safety") &&
              renderModuleButton({
                text: "Safety",
                to: "/safety",
                icon: <ReportProblemIcon />,
                selected: isSafetyPath,
              })}
            {role?.some((r) =>
              ["Operations", "OperationsManager", "OperationsAdmin"].includes(
                r,
              ),
            ) &&
              operationsMenuItems.map((item) => (
                <React.Fragment key={item.text}>
                  {renderModuleButton({
                    text: item.text,
                    to: item.path,
                    icon: item.icon,
                    selected: location.pathname.startsWith(item.path),
                  })}
                </React.Fragment>
              ))}
            {role?.includes("Permits") &&
              renderModuleButton({
                text: "Permits",
                to: "/permits",
                icon: <RouteIcon />,
                selected: isPermitsPath,
              })}
            {(role?.includes("FleetCRM") || role?.includes("HireCRM")) &&
              renderModuleGroup({
                text: "CRM",
                icon: <CarRentalIcon />,
                selected: isCRMPath,
                items: [
                  ...(role?.includes("FleetCRM") ? fleetCRMMenuItems : []),
                  ...(role?.includes("HireCRM") ? hireCRMMenuItems : []),
                ],
              })}
            {role?.includes("Admin") &&
              adminMenuItems.map((item) => (
                <React.Fragment key={item.text}>
                  {renderModuleButton({
                    text: item.text,
                    to: item.path,
                    icon: item.icon,
                    selected: location.pathname === item.path,
                  })}
                </React.Fragment>
              ))}
          </Box>
          {compactHeader && (
            <Button
              startIcon={<AppsIcon />}
              onClick={() => setModulesOpen(true)}
              aria-label="Open modules"
              aria-expanded={modulesOpen}
            >
              Modules
            </Button>
          )}
          <LogoutButton />
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="right"
        open={modulesOpen && compactHeader}
        onClose={() => setModulesOpen(false)}
      >
        <Box sx={{ width: 300, maxWidth: "calc(100vw - 32px)", pt: "64px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
            }}
          >
            <Typography variant="h6">Modules</Typography>
            <IconButton
              aria-label="Close modules"
              onClick={() => setModulesOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <List aria-label="Modules">
            {mobileModules.map((item) => (
              <ListItemButton
                key={item.path}
                component={RouterLink}
                to={item.path}
                selected={isPathSelected(item.path)}
                onClick={() => setModulesOpen(false)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
      {renderedSidebar}
      <Box
        component="main"
        className="app-content"
        sx={{
          p: { xs: 1.5, sm: 2, lg: 3 },
          minWidth: 0,
          maxWidth: "100%",
          mt: "64px",
          ml:
            renderedSidebar && !isSmallScreen
              ? sidebarOpen
                ? `${drawerWidth}px`
                : `${collapsedWidth}px`
              : 0,
          transition: (theme) =>
            theme.transitions.create(["margin-left"], {
              duration: theme.transitions.duration.standard,
            }),
        }}
      >
        {children}
      </Box>
    </>
  );
};

export default NavigationBar;
